import { useCheckAccountTraits } from '@cmg/auth';
import {
  BusinessCenterOutlinedIcon,
  NoteAltOutlinedIcon,
  RequestQuoteOutlinedIcon,
} from '@cmg/design-system';
import { ReactNode, useMemo } from 'react';

import { useIsSmallScreen } from '../../../common/hooks/useIsSmallScreen';
import useCheckOrderBookAccess from '../../../common/util/check-access/useCheckOrderBookAccess';
import { useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess } from '../../../common/util/check-access/useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess';
import { useCmgEntityKeyContext } from '../contexts/CmgEntityKeyContext';
import { useSupersededOfferingContext } from '../contexts/SupersededOfferingContext';
import offeringSidePanelBuySideRouteFactory from '../offeringSidePanelBuySideRouteFactory';
import offeringSidePanelSellSideRouteFactory from '../offeringSidePanelSellSideRouteFactory';
import {
  OrderBookPersonaType,
  useIdentifyOrderBookPersona,
} from '../order-book/hooks/useIdentifyOrderBookPersona';
import { useOfferingSidePanelContext } from '../order-book/indication-activity/context-old/OfferingSidePanelContext';
import { useOfferingSidePanelRouteMatch } from './useOfferingSidePanelMatch';

type UseSidePanelNavItemsProps = {
  offeringId: string;
  baseSidePanelUrl: string;
};

export type NavItem = {
  readonly id: string;
  readonly href: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly selected: boolean;
  readonly testId?: string;
};

export type NavItemGroup = {
  readonly id: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly items: NavItem[];
  readonly routePath: string;
  readonly testId?: string;
  readonly href?: string;
};

export function useSidePanelSellSideNavItems({
  offeringId,
  baseSidePanelUrl,
}: UseSidePanelNavItemsProps) {
  const { canUpdateIndications, type } = useIdentifyOrderBookPersona({ offeringId });
  const canViewSyndicateNotes = useCheckAccountTraits(['XC_IOI_NOTES']);
  const { canRead: canReadOrderBook } = useCheckOrderBookAccess({ offeringId });
  const { cmgEntityKey } = useCmgEntityKeyContext();
  const { canRead: canReadIndications } =
    useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess({ offeringId });
  const { indication } = useOfferingSidePanelContext();
  const { isFiledOffering } = useSupersededOfferingContext();

  const matchesOfferingDetails = useOfferingSidePanelRouteMatch(
    offeringSidePanelSellSideRouteFactory.offeringDetails.routePath
  );
  const matchesCommunicatedDemand = useOfferingSidePanelRouteMatch(
    offeringSidePanelSellSideRouteFactory.orderBookIndicationActivity.routePath
  );

  const matchesUpdateHistory = useOfferingSidePanelRouteMatch(
    offeringSidePanelSellSideRouteFactory.orderBookUpdateHistory.routePath
  );
  const matchesCompliance = useOfferingSidePanelRouteMatch(
    offeringSidePanelSellSideRouteFactory.orderBookCompliance.routePath
  );
  const matchesNotes = useOfferingSidePanelRouteMatch(
    offeringSidePanelSellSideRouteFactory.orderBookNotes.routePath
  );
  const matchesSummary = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.offeringSummary.routePath
  );

  const isSmallScreen = useIsSmallScreen();

  const navItems: Array<NavItem | NavItemGroup> = useMemo(() => {
    const items: Array<NavItem | NavItemGroup> = [];
    if (isSmallScreen) {
      items.push({
        id: 'summary',
        label: 'Summary',
        href: baseSidePanelUrl + offeringSidePanelBuySideRouteFactory.offeringSummary.getUrlPath(),
        selected: matchesSummary,
      });
    }
    items.push({
      id: 'offering-details',
      label: 'Offering Details',
      href:
        baseSidePanelUrl +
        offeringSidePanelSellSideRouteFactory.offeringDetails.getUrlPath({
          cmgEntityKey,
        }),
      icon: <BusinessCenterOutlinedIcon />,
      selected: matchesOfferingDetails,
    });

    if (canReadOrderBook) {
      const orderBookNavItemGroup: NavItemGroup = {
        id: 'order-book',
        label: 'Order Book',
        icon: <RequestQuoteOutlinedIcon />,
        items: [],
        routePath: offeringSidePanelSellSideRouteFactory.orderBook.routePath,
      };

      if (canReadIndications && !isFiledOffering) {
        orderBookNavItemGroup.items.push({
          id: 'communicated-demand',
          label: 'Communicated Demand',
          href:
            baseSidePanelUrl +
            offeringSidePanelSellSideRouteFactory.orderBookIndicationActivity.getUrlPath({
              cmgEntityKey,
            }),
          selected: matchesCommunicatedDemand,
        });

        if (indication.data) {
          orderBookNavItemGroup.items.push({
            id: 'update-history',
            label: 'Update History',
            href:
              baseSidePanelUrl +
              offeringSidePanelSellSideRouteFactory.orderBookUpdateHistory.getUrlPath({
                cmgEntityKey: cmgEntityKey!,
              }),
            selected: matchesUpdateHistory,
          });
        }

        orderBookNavItemGroup.items.push({
          id: 'compliance',
          label: 'Compliance',
          href:
            baseSidePanelUrl +
            offeringSidePanelSellSideRouteFactory.orderBookCompliance.getUrlPath({
              cmgEntityKey,
            }),
          selected: matchesCompliance,
        });

        items.push(orderBookNavItemGroup);
      }
    }

    if (
      type !== OrderBookPersonaType.SALES_AND_TRADING &&
      canUpdateIndications &&
      canViewSyndicateNotes
    ) {
      items.push({
        id: 'syndicate-notes',
        label: 'Notes',
        icon: <NoteAltOutlinedIcon />,
        href:
          baseSidePanelUrl +
          offeringSidePanelSellSideRouteFactory.orderBookNotes.getUrlPath({ cmgEntityKey }),
        selected: matchesNotes,
      });
    }
    return items;
  }, [
    baseSidePanelUrl,
    cmgEntityKey,
    indication.data,
    isFiledOffering,
    matchesCommunicatedDemand,
    matchesCompliance,
    matchesNotes,
    matchesUpdateHistory,
    matchesOfferingDetails,
    matchesSummary,
    type,
    canReadOrderBook,
    canReadIndications,
    canUpdateIndications,
    canViewSyndicateNotes,
    isSmallScreen,
  ]);

  return navItems;
}
